/**
 * TeamAssignmentPicker (Phase 2.7)
 *
 * Single-select dropdown for picking a step assignee (executor or
 * approver). Honours the doctor's team membership:
 *   - When the doctor has a team defined, options are restricted to
 *     team members matching `roleFilter` (or any role if not provided).
 *   - When the doctor has NO team, options fall back to every staff
 *     member matching `roleFilter` — same default-open rule the backend
 *     enforces (DoctorTeamService.is_member_on_team / validate_assignees).
 *
 * Doctor-as-self ("Default to me") is always offered as the first option.
 * The doctor is implicitly on their own team server-side, so this is
 * always a valid pick.
 *
 * Follows the Phase 2.5 contrast + button-visibility rules from CLAUDE.md.
 */
import React, { useMemo } from 'react';

import useDoctorTeamStore from '../../stores/doctorTeamStore';
import { filterProvidersByDept } from '../visit/departmentResolution';


export default function TeamAssignmentPicker({
  doctorMemberId,
  doctorName,
  value,
  onChange,
  roleFilter,           // 'NURSE' | 'DOCTOR' | undefined
  staff = [],           // Array<{member_id, role, name}> — full clinic roster fallback
  placeholder = 'Pick an assignee…',
  highlight = false,    // Render with a red border (MISSING_ASSIGNEES recovery flow)
  disabled = false,
  required = false,
  // A2 completion — dept-scope the step-assignee options to the VISIT'S stamped
  // department of care. Anchor = visit.department_id; falsy → no filter (org with
  // 0 depts, or a no/emergency-dept visit). deptsByMember = the shared member→dept
  // map (departmentStore). Applied to the candidate pool ONLY — the owner-doctor
  // "Me" self-option is always kept (a doctor can always own/approve their own
  // plan's steps). Single source: shared filterProvidersByDept (keeps no-dept
  // staff org-wide, excludes other-dept, fail-opens on un-hydrated edges).
  visitDepartmentId = null,
  deptsByMember = null,
}) {
  const teamsByDoctor = useDoctorTeamStore((s) => s.teamsByDoctor);
  const team = teamsByDoctor[doctorMemberId];

  const options = useMemo(() => {
    const seen = new Set();
    const out = [];

    // 1. Doctor-as-self always first (never dept-filtered — self is always valid).
    if (doctorMemberId) {
      out.push({
        member_id: doctorMemberId,
        role: 'DOCTOR',
        label: `Me${doctorName ? ` (${doctorName})` : ''}`,
        is_self: true,
      });
      seen.add(doctorMemberId);
    }

    // 2. Team members (when defined) OR every staff member (when not).
    const rawPool = team
      ? (team.members || []).map((m) => ({
          member_id: m.member_id,
          role: m.role,
          label:
            (staff.find((s) => s.member_id === m.member_id)?.name)
            || m.label
            || m.member_id,
        }))
      : staff.map((s) => ({
          member_id: s.member_id,
          role: s.role,
          label: s.name || s.member_id,
        }));

    // A2 — dept-scope the candidate pool (self already added above, excluded here).
    const pool = filterProvidersByDept(rawPool, visitDepartmentId, deptsByMember || {});

    for (const p of pool) {
      if (seen.has(p.member_id)) continue;
      if (roleFilter && p.role && p.role !== roleFilter) continue;
      out.push(p);
      seen.add(p.member_id);
    }
    return out;
  }, [team, staff, doctorMemberId, doctorName, roleFilter, visitDepartmentId, deptsByMember]);

  const borderClass = highlight
    ? 'border-red-500 dark:border-red-400 ring-1 ring-red-300'
    : 'border-slate-300 dark:border-slate-600';

  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value || null)}
      disabled={disabled}
      required={required}
      className={
        'w-full text-sm font-medium px-2.5 py-1.5 rounded-md border '
        + borderClass
        + ' bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 '
        + 'disabled:opacity-60'
      }
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o.member_id} value={o.member_id}>
          {o.label}{o.role && !o.is_self ? ` · ${o.role}` : ''}
        </option>
      ))}
    </select>
  );
}
